<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import {
  asApiError,
  gameNotOpenMessage,
  rateLimitMessage,
  statusFrom,
  userMessage,
} from '@/api/errors';
import {
  blankJoinForm,
  JOIN_LIMITS,
  normaliseGameCode,
  validateJoin,
  type JoinFieldErrors,
  type JoinForm,
} from '@/api/join';
import { formatDateTime } from '@/api/datetime';
import { formatMoney, gridSummary, playsForMoney } from '@/api/grid';
import FormField from '@/components/FormField.vue';
import { focusFirstError } from '@/composables/focusFirstError';
import { i18n, t } from '@/i18n';
import { useGamesStore } from '@/stores/games';
import { useSessionStore } from '@/stores/session';

/*
 * From a shared link to a name to the grid. The name field is the entire signup — do not add
 * fields to it. See docs/UX_FLOWS.md.
 */

const props = defineProps<{ code?: string }>();

const router = useRouter();
const games = useGamesStore();
const session = useSessionStore();

/*
 * Empty of a name at setup, and filled in `onMounted` once the session has answered. The store
 * is very often still resolving when this runs now — see the hook.
 */
const form = reactive<JoinForm>(
  blankJoinForm(props.code ?? '', session.subject?.displayName ?? ''),
);
const errors = ref<JoinFieldErrors>({});
const message = ref<string | null>(null);
/** The form element, for moving the cursor onto whichever field is being complained about. */
const formEl = ref<HTMLFormElement | null>(null);
/** Set when the game exists but is not accepting players, so the grid is still worth offering. */
const lockedCode = ref<string | null>(null);

/*
 * What this pool is, for somebody who was sent a link and has been told nothing.
 *
 * `GET /games/{code}` needs no session — the route says so — so there was never a reason to ask a
 * stranger for a name first. They committed a display name and a player row with no idea of the
 * fixture, the price, the fill or the kick-off, and met "R10 a square" on the next screen.
 *
 * Read off the store the membership check already fills, so this costs no extra request.
 */
const about = computed(() => {
  const detail = games.current;
  const game = detail?.game;
  if (!game || game.code !== arrivedWithCode) return null;

  /*
   * Said once, not twice: a host who leaves the name blank gets `{teamA} vs {teamB}` computed for
   * them, which is the fixture — so printing both put the same sentence under itself.
   */
  const fixture = t('myGames.fixture', { teamA: game.teamA, teamB: game.teamB });

  return {
    name: game.name,
    fixture: fixture === game.name ? null : fixture,
    forMoney: playsForMoney(game),
    price: formatMoney(game.pricePerSquare ?? 0, game.currency ?? ''),
    fill: gridSummary(detail?.squares.length ?? 0, game.squaresClaimed ?? 0),
    kickOff: formatDateTime(game.kickOffAt, i18n.global.locale.value),
    kickOffAt: game.kickOffAt ?? undefined,
  };
});

/**
 * Where sign-in should return to. Built from the code as typed rather than the prop, so somebody
 * who pasted a code into the box does not lose it by signing in.
 */
const signInLink = computed(() => {
  const code = normaliseGameCode(form.code);
  return `/sign-in?redirect=${code ? `/join/${code}` : '/join'}`;
});

/*
 * The link gets shared once and tapped many times — from the group chat, from a bookmark, from
 * whatever the browser remembered. Somebody who already has a player row has nothing to tell us,
 * so this screen is a form standing between them and the grid.
 *
 * Set here rather than inside the hook: `onMounted` fires after the first render, so flipping it
 * there would flash the form for a tick and then navigate away from it.
 *
 * **Arriving with a code is the whole condition, and it did not used to be.** It was this *and*
 * `session.isSignedIn`, which was a fair reading while the router guard resolved /me before any
 * view rendered. It no longer does on a route with no gate — that round trip is what put the
 * landing page's CLS at 1.0, see src/router.ts — so this screen is now mounted while the answer
 * is still in flight, and asking a store that has not answered gets "nobody". The result was the
 * shared link's worst case: somebody already in the game shown the form they had been past.
 */
const arrivedWithCode = normaliseGameCode(props.code ?? '');
const checking = ref(Boolean(arrivedWithCode));

/*
 * Membership is a player row, not a session and not being the host: creating a game writes no
 * player row, so a host who has not joined their own game still belongs on this form.
 */
onMounted(async () => {
  /*
   * Who this is, before anything is decided about them. Idempotent and shared — if the guard
   * started this call on the way in, which it did, this waits on that one rather than making a
   * second. See the session store.
   */
  try {
    await session.load();
  } catch {
    // Unprovable is the same as nobody here: the form is what somebody with no session needs,
    // and it is what the failure leaves them on.
  }

  /*
   * The name, now that there is one to have. Only into a field nobody has touched: with no code
   * in the link this screen renders its form immediately, so somebody can be typing by the time
   * the session lands, and their own typing outranks what we remembered about them.
   */
  if (!form.displayName) form.displayName = session.subject?.displayName ?? '';

  if (!checking.value) return;

  try {
    /*
     * Read for *everybody* who arrived with a code, not just for somebody with a session. It was
     * a membership check, which only a session can fail; it is also the only description of the
     * pool this screen can offer, and the stranger is exactly who needs one.
     */
    await games.load(arrivedWithCode);
    const detail = games.current;
    const me = session.isSignedIn ? (detail?.you?.subjectId ?? null) : null;

    if (me !== null && detail?.players.some((player) => player.subjectId === me)) {
      // replace, not push: this screen had nothing to offer them, so Back belongs wherever they
      // came from rather than bouncing them into a form they have already been past.
      await router.replace({ name: 'game', params: { code: arrivedWithCode } });
      return;
    }

    // A code that will not read is most likely mistyped, and the form is where that gets fixed —
    // so do not leave the failure behind for the next screen to inherit.
    if (games.error) games.clear();
  } finally {
    checking.value = false;
  }
});

async function onSubmit(): Promise<void> {
  if (games.saving) return;

  message.value = null;
  lockedCode.value = null;
  errors.value = validateJoin(form);
  if (Object.keys(errors.value).length > 0) {
    // In reading order, so a bad code and a blank name land the cursor on the code.
    focusFirstError(formEl.value, ['code', 'displayName'], errors.value);
    return;
  }

  const code = normaliseGameCode(form.code);
  const displayName = form.displayName.trim();

  try {
    // A session first, because joining needs a subject for the player row to belong to. Guest
    // creation is the entire signup, and it happens without the player being asked anything.
    if (!session.isSignedIn) await session.continueAsGuest(displayName);

    await games.join(code, displayName);
    router.push({ name: 'game', params: { code } });
  } catch (caught) {
    const error = asApiError(caught);

    if (error.code === 'GAME_NOT_OPEN') {
      // Worth a look even so — a locked grid still shows who holds what.
      lockedCode.value = code;
      message.value = gameNotOpenMessage(statusFrom(error.details));
      return;
    }

    if (error.code === 'RATE_LIMITED') {
      message.value = rateLimitMessage(error, 'join.tooManyJoins');
      return;
    }

    // Everything else keeps the form exactly as typed.
    message.value = userMessage(error.code, error.message);
  }
}
</script>

<template>
  <section class="max-w-md">
    <h1 class="text-2xl font-semibold">{{ t('join.heading') }}</h1>

    <!--
      Nothing but the heading while we find out whether this person is already in the game.
      Rendering the form and then navigating off it would be a flash of something to fill in.
    -->
    <p v-if="checking" class="mt-2 text-ink-muted" aria-busy="true">{{ t('join.checking') }}</p>

    <template v-else>
      <!--
        First, and before the form asks for anything: the fixture, what a square costs, how full
        the grid is and when the match is. A stranger who was sent a link and told "it's just for
        fun" had none of it, and met the price on the next screen.
      -->
      <section v-if="about" class="mt-4 rounded-lg border border-line bg-surface p-4">
        <h2 class="font-semibold">{{ about.name }}</h2>
        <p v-if="about.fixture" class="mt-1 text-sm">{{ about.fixture }}</p>
        <p class="mt-1 text-sm text-ink-muted">
          {{
            about.forMoney ? t('join.about.price', { amount: about.price }) : t('join.about.free')
          }}
        </p>
        <p class="mt-1 text-sm text-ink-muted">{{ about.fill }}</p>
        <p v-if="about.kickOff" class="mt-1 text-sm text-ink-muted">
          {{ t('myGames.kickOff') }}
          <time :datetime="about.kickOffAt">{{ about.kickOff }}</time>
        </p>
      </section>

      <p class="mt-2 text-ink-muted">{{ t('join.intro') }}</p>

      <form ref="formEl" class="mt-6 space-y-4" @submit.prevent="onSubmit">
        <FormField
          v-model="form.code"
          name="code"
          :label="t('join.codeLabel')"
          :hint="t('join.codeHint')"
          :error="errors.code"
          :maxlength="12"
          :placeholder="t('join.codePlaceholder')"
          autocapitalize="characters"
          autocomplete="off"
          :spellcheck="false"
          enterkeyhint="go"
        />
        <FormField
          v-model="form.displayName"
          name="displayName"
          :label="t('join.nameLabel')"
          :hint="t('join.nameHint')"
          :error="errors.displayName"
          :maxlength="JOIN_LIMITS.displayNameMax"
          :placeholder="t('join.namePlaceholder')"
        />

        <button
          type="submit"
          :disabled="games.saving"
          class="w-full rounded bg-accent px-4 py-3 font-medium text-on-accent hover:bg-accent-strong disabled:border-line disabled:bg-surface-sunken disabled:text-ink-muted"
        >
          {{ games.saving ? t('join.submitting') : t('join.submit') }}
        </button>
      </form>

      <!--
      The trap in the API's docs/AUTH.md: verifying an address that already has an account
      switches this browser onto that account's subject, and squares held by the guest subject
      are left behind. Signing in first is the cheap way out, so it is offered here — where the
      decision is actually made — rather than mentioned later.
    -->
      <p v-if="!session.isSignedIn" class="mt-6 text-sm text-ink-muted">
        {{ t('join.haveAccount') }}
        <RouterLink :to="signInLink" class="font-medium underline hover:text-ink">
          {{ t('join.signInFirst') }}
        </RouterLink>
      </p>

      <p aria-live="polite" class="mt-4 min-h-6 text-sm font-medium text-ink">
        {{ message }}
      </p>

      <RouterLink
        v-if="lockedCode"
        :to="`/game/${lockedCode}`"
        class="inline-block rounded border border-accent px-4 py-2 text-sm font-medium hover:bg-fill"
      >
        {{ t('join.seeTheGrid') }}
      </RouterLink>
    </template>
  </section>
</template>
